import { Component, HostBinding, ChangeDetectionStrategy, ViewEncapsulation, OnInit, EventEmitter, Output, Input,
    ChangeDetectorRef, forwardRef } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseDirective } from '../../base.directive';
import { EuiDialogConfig, EuiDialogInjectedComponentInstances, EuiDialogService } from '../eui-dialog';
import { EuiPickerItem } from './models/eui-picker-item.model';
import { EuiModalPickerComponent } from './modal-picker/eui-modal-picker.component';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    templateUrl: './eui-picker.component.html',
    selector: 'eui-picker',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./styles/_index.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => EuiPickerComponent),
        multi: true,
    }],
})
export class EuiPickerComponent extends BaseDirective implements OnInit, ControlValueAccessor {

    @Input()
    get hasSearchFilter() { return this._hasSearchFilter; }
    set hasSearchFilter(value) { this._hasSearchFilter = coerceBooleanProperty(value); }
    _hasSearchFilter = false;

    @Input()
    get hasNbSelectedInfo() { return this._hasNbSelectedInfo; }
    set hasNbSelectedInfo(value) { this._hasNbSelectedInfo = coerceBooleanProperty(value); }
    _hasNbSelectedInfo = false;

    @Input() placeholder = '';
    @Input() modalTitle = '';

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-picker'),
        ].join(' ').trim();
    }

    @Input() pickerTitle: string;
    @Input() items: EuiPickerItem[] = [];

    @Output() change: EventEmitter<EuiPickerItem[]> = new EventEmitter<EuiPickerItem[]>();
    @Output() itemsChange = new EventEmitter<{
        added: EuiPickerItem,
        removed: EuiPickerItem,
        items: EuiPickerItem[],
        selected: EuiPickerItem[],
        unselected: EuiPickerItem[],
    }>();

    itemsSelected: EuiPickerItem[] = [];
    itemsState: {
        added: EuiPickerItem,
        removed: EuiPickerItem,
        items: EuiPickerItem[],
        selected: EuiPickerItem[],
        unselected: EuiPickerItem[],
    };
    isDisabled = false;

    @Output() accept: EventEmitter<any> = new EventEmitter();
    @Output() dismiss: EventEmitter<any> = new EventEmitter();
    @Output() close: EventEmitter<any> = new EventEmitter();
    @Output() clickOutside: EventEmitter<any> = new EventEmitter();

    constructor(private euiDialogService: EuiDialogService, private cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.itemsState = {
            ...this.itemsState,
            items: this.items,
            selected: this.items.filter(item => item.isSelected),
        };
    }

    public writeValue(value: EuiPickerItem[]) {
        this.itemsState = {
            ...this.itemsState,
            items: value ? this.items.map(i => value.find(is => is.id === i.id) ? { ...i, isSelected: true } : i) : this.items,
            selected: value ? value.map(v => { return { ...v, isSelected: true }; }) : [],
        };
        this.cd.detectChanges();
    }

    public setDisabledState(isDisabled: boolean) {
        isDisabled ? this.isDisabled = true : this.isDisabled = false;
        this.cd.detectChanges();
    }

    public registerOnChange(fn: any) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    openModal() {
        if (!this.isDisabled) {
            let itemsState = this.itemsState;
            const config = new EuiDialogConfig({
                title: this.modalTitle,
                height: '30vh',
                width: '60vw',
                isHandleCloseOnClickOutside: true,
                bodyComponent: {
                    component: EuiModalPickerComponent,
                    config: {
                        items: this.itemsState.items,
                        placeholder: this.placeholder,
                        hasSearchFilter: this.hasSearchFilter,
                        itemsChange: (e: {
                            added: EuiPickerItem,
                            removed: EuiPickerItem,
                            items: EuiPickerItem[],
                            selected: EuiPickerItem[],
                            unselected: EuiPickerItem[],
                        }) => {
                            itemsState = e;
                            this.itemsChange.emit(e);
                        }
                    },
                },
                onClickOutside: (dialogRef: MatDialogRef<any>, componentInstances: EuiDialogInjectedComponentInstances) => {
                    this.euiDialogService.closeDialog(dialogRef);
                    this.clickOutside.emit();
                },
                onClick: () => {},
                onDismiss: () => {
                    this.dismiss.emit();
                },
                onAccept: () => {
                    this.itemsState = itemsState;
                    this.onChange(this.itemsState.selected);
                    this.accept.emit();

                    this.cd.detectChanges();
                },
                onClose: () => {
                    this.close.emit();
                },
            });

            this.euiDialogService.openDialog(config);
        }
    }

    private onChange: any = () => {};

    private onTouch: any = () => {};
}
